import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, subscribeToInventory, subscribeToLeads, subscribeToTenants, unsubscribeChannel, logActivity, predictOccupancyDrop, calculateRenewalChurnProbability, generateDynamicPricingSuggestions } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import KPICard from './components/KPICard';
import VacantInventoryWidget from './components/VacantInventoryWidget';
import ExpiringLockinsWidget from './components/ExpiringLockinsWidget';
import LeadsPipelineWidget from './components/LeadsPipelineWidget';
import VacancyPredictionAlerts from './components/VacancyPredictionAlerts';
import ChurnRiskWarnings from './components/ChurnRiskWarnings';
import DynamicPricingAlerts from './components/DynamicPricingAlerts';
import AIInsightsCard from './components/AIInsightsCard';
import BreakEvenMeter from './components/BreakEvenMeter';
import ControlRoomKPIBar from './components/ControlRoomKPIBar';

const Dashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [aiData, setAiData] = useState({
    predictions: [],
    churnData: [],
    pricingSuggestions: []
  });
  const [centerFinancials, setCenterFinancials] = useState(null);

  // Role-based access flags
  const isSales = userProfile?.role === 'sales';
  const isCenterManager = userProfile?.role === 'center_manager';
  const isSuperAdmin = userProfile?.role === 'super_admin';
  const assignedCenters = userProfile?.assigned_centers || userProfile?.assignedCenters || [];

  useEffect(() => {
    loadDashboardData();

    // Subscribe to real-time changes
    const inventoryChannel = subscribeToInventory(() => {
      loadDashboardData();
    });

    const leadsChannel = subscribeToLeads(() => {
      loadDashboardData();
    });

    const tenantsChannel = subscribeToTenants(() => {
      loadDashboardData();
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeChannel(inventoryChannel);
      unsubscribeChannel(leadsChannel);
      unsubscribeChannel(tenantsChannel);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load dashboard stats and AI predictions in parallel
      const [data, occupancyPredictions, churnProbability, pricingSugg] = await Promise.all([
        getDashboardStats(),
        predictOccupancyDrop(),
        calculateRenewalChurnProbability(),
        generateDynamicPricingSuggestions()
      ]);
      
      setStats(data);
      setAiData({
        predictions: occupancyPredictions,
        churnData: churnProbability,
        pricingSuggestions: pricingSugg
      });

      // Load center financials for break-even meter
      try {
        const { supabase: sb } = await import('../../lib/supabase');
        const { data: centers } = await sb?.from('centers')?.select('id, name, fixed_monthly_operating_cost, target_revenue, total_building_sqft, available_bareshell_sqft')?.eq('is_active', true)?.limit(1);
        const { data: agreements } = await sb?.from('agreements')?.select('rent_amount, center_id')?.eq('status', 'active');
        const { data: inventory } = await sb?.from('inventory')?.select('status, center_id');

        if (centers && centers?.length > 0) {
          const center = centers?.[0];
          const totalRent = (agreements || [])?.reduce((sum, a) => sum + (parseFloat(a?.rent_amount) || 0), 0);
          const totalUnits = (inventory || [])?.length;
          const occupiedUnits = (inventory || [])?.filter(u => u?.status === 'occupied')?.length;
          const occupancyPct = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;

          setCenterFinancials({
            centerId: center?.id,
            centerName: center?.name,
            totalBuildingSqft: center?.total_building_sqft || 0,
            availableBareshellSqft: center?.available_bareshell_sqft || 0,
            occupancyPercent: occupancyPct,
            occupancyTrend: occupancyPct >= 70 ? 'up' : 'down',
            monthlyRevenue: totalRent,
          });
        }
      } catch (finErr) {
        console.log('Financial data load skipped:', finErr?.message);
      }
      
      // Log dashboard view activity
      await logActivity({
        module: 'dashboard',
        action: 'export',
        entityType: 'dashboard_view',
        entityName: 'Dashboard',
        description: 'Viewed dashboard metrics'
      });
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Filter AI alerts for center_manager: only show alerts relevant to their assigned centers
  const getFilteredPredictions = () => {
    if (!isCenterManager || !assignedCenters?.length) return aiData?.predictions;
    return aiData?.predictions?.filter(p =>
      !p?.centerId || assignedCenters?.includes(p?.centerId)
    );
  };

  const getFilteredChurnData = () => {
    if (!isCenterManager || !assignedCenters?.length) return aiData?.churnData;
    return aiData?.churnData?.filter(t =>
      !t?.centerId || assignedCenters?.includes(t?.centerId)
    );
  };

  const getFilteredPricingSuggestions = () => {
    if (!isCenterManager || !assignedCenters?.length) return aiData?.pricingSuggestions;
    return aiData?.pricingSuggestions?.filter(s =>
      !s?.centerId || assignedCenters?.includes(s?.centerId)
    );
  };

  const filteredPredictions = getFilteredPredictions();
  const filteredChurnData = getFilteredChurnData();
  const filteredPricingSuggestions = getFilteredPricingSuggestions();

  const occupancyPercentage = stats?.inventory?.total > 0
    ? ((stats?.inventory?.occupied / stats?.inventory?.total) * 100)?.toFixed(1)
    : 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Control Room KPI Bar - visible to admin/super_admin */}
      {!isSales && centerFinancials && (
        <ControlRoomKPIBar stats={centerFinancials} />
      )}
      {/* AI-Powered Insights Section */}
      <div className="mb-6">
        <AIInsightsCard 
          metrics={{
            occupancyRate: stats?.occupancyRate || 0,
            // Hide revenue from sales role
            revenue: isSales ? undefined : (stats?.totalRevenue || 0),
            leads: stats?.activeLeads || 0,
            expiringLeases: stats?.expiringLeases || 0,
            vacantUnits: stats?.vacantUnits || 0
          }}
        />
      </div>
      {/* AI-Powered Alert Cards - filtered by assigned centers for center_manager */}
      {(filteredPredictions?.length > 0 || filteredChurnData?.length > 0 || filteredPricingSuggestions?.length > 0) && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-xl font-bold text-foreground">AI-Powered Alerts</h2>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">LIVE</span>
          </div>
          <div className="space-y-4">
            {filteredPredictions?.length > 0 && (
              <VacancyPredictionAlerts predictions={filteredPredictions} />
            )}
            {filteredChurnData?.length > 0 && (
              <ChurnRiskWarnings churnData={filteredChurnData} />
            )}
            {/* Hide dynamic pricing alerts from sales role */}
            {!isSales && filteredPricingSuggestions?.length > 0 && (
              <DynamicPricingAlerts suggestions={filteredPricingSuggestions} />
            )}
          </div>
        </div>
      )}
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <KPICard
          title="Total Inventory"
          value={stats?.inventory?.total || 0}
          icon="Package"
          trend="neutral"
          subtitle=""
          trendValue=""
          iconColor=""
          status=""
        />
        <KPICard
          title="Occupancy Rate"
          value={`${occupancyPercentage}%`}
          icon="TrendingUp"
          trend={parseFloat(occupancyPercentage) >= 70 ? 'up' : 'down'}
          subtitle={parseFloat(occupancyPercentage) >= 70 ? 'Above target' : 'Below target'}
          trendValue=""
          iconColor=""
          status=""
        />
        <KPICard
          title="Vacant Units"
          value={stats?.inventory?.vacant || 0}
          icon="AlertCircle"
          trend="neutral"
          subtitle=""
          trendValue=""
          iconColor=""
          status=""
        />
        {/* Hide Monthly Revenue KPI from sales role */}
        {!isSales && (
          <KPICard
            title="Monthly Revenue"
            value={`₹${((stats?.tenants?.monthlyRevenue || 0) / 100000)?.toFixed(1)}L`}
            icon="IndianRupee"
            trend="up"
            subtitle=""
            trendValue=""
            iconColor=""
            status=""
          />
        )}
      </div>
      {/* Break-even Meter - visible to admin/super_admin */}
      {!isSales && centerFinancials?.centerId && (
        <div className="mb-6">
          <BreakEvenMeter
            centerId={centerFinancials?.centerId}
            centerName={centerFinancials?.centerName}
          />
        </div>
      )}
      {/* Widgets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <VacantInventoryWidget 
          vacantUnits={stats?.inventory?.vacant || 0}
          vacantCount={stats?.inventory?.vacant || 0}
          onViewAll={() => navigate('/inventory-management')}
          onViewVacant={() => navigate('/inventory-management')}
        />
        <ExpiringLockinsWidget 
          expiringTenants={stats?.expiringLockIns || []} 
          onViewAll={() => navigate('/tenant-management')}
        />
      </div>
      {/* Leads Pipeline Widget */}
      <div className="mt-6">
        <LeadsPipelineWidget 
          leadsByStage={[
            { stage: 'New', count: stats?.leads?.total || 0 },
            { stage: 'Active', count: stats?.leads?.active || 0 },
            { stage: 'Won', count: stats?.leads?.won || 0 }
          ]} 
          onViewAll={() => navigate('/leads-pipeline')}
        />
      </div>
    </div>
  );
};

export default Dashboard;